import { localStorage } from '../../../../shared/localstorage';
import UserBehaviorLogger, { IUserBehavioralEvent } from '../../../../shared/UserBehaviorLogger';

/**
 * 仕様書: https://wiki.workers-hub.com/pages/viewpage.action?pageId=1454288993
 */
interface IULTParamater {
  /**
   * 当日を含む直近7日間で何日訪問したか
   */
  pageViewDaysInLast7days: number;
  /**
   * 直近のコメント接触日時(unixtime)
   */
  newestCommentViewTime?: number;
  /**
   * ユーザのコメントモジュールの表示設定(非表示0,表示1)
   * 文字列で落とす
   */
  userCommentDisplaySetting?: '0' | '1';
  /**
   * コメントのGood/Badを押した回数
   */
  userCommentVoteCount: number;
  /**
   * コメントを投稿した回数
   */
  userCommentPostCount: number;
}
/**
 * localストレージに保存されている情報でultに使用するログデータを取得する。
 *
 * @param pageParam
 * @param pageParam.pagetype
 * @param pageParam.opttype
 * @param pageParam.conttype
 * @returns {
 *   pageViewDaysInLast7days: number; // 当日を含む直近7日間で何日訪問したか
 *   newestCommentViewTime?: string; //直近のコメント接触日時(unixtime)
 *   userCommentDisplaySetting?: '0' | '1' // ユーザのコメントモジュールの表示設定(非表示0,表示1)
 * }
 */
export const getUltLogByLocalStorage = (pageParam: {
  pagetype: string;
  opttype: string;
  conttype?: string;
}): IULTParamater => {
  const logEvents: IUserBehavioralEvent[] = UserBehaviorLogger.getLogs();
  return {
    pageViewDaysInLast7days: getPageViewDateCountInLast7days(logEvents),
    newestCommentViewTime: getCommentViewableDate(logEvents),
    userCommentDisplaySetting:
      // ユーザのコメント非表示設定はPCのトピ詳細(topics)・記事詳細(article/mgznart)のみ取得対象
      pageParam.opttype === 'pc' &&
      pageParam.pagetype === 'detail' &&
      pageParam.conttype !== undefined &&
      ['topics', 'article', 'mgznart'].indexOf(pageParam.conttype) !== -1
        ? getCommentModuleDisplayConfig()
        : undefined,
    userCommentVoteCount: getCommentVoteCount(logEvents),
    userCommentPostCount: getCommentPostCount(logEvents)
  };
};

/**
 * 7日以内のPVログの日数を返す。
 * ※当日も1日に入れる
 *
 * @param logEvents
 */
const getPageViewDateCountInLast7days = (logEvents: IUserBehavioralEvent[]): number => {
  const uniquedElapsedDays: number[] = [];
  const nowDate = new Date();
  for (const log of logEvents) {
    const diffTime = nowDate.getTime() - log.datetime;
    const elaplsedDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (6 < elaplsedDays) {
      break;
    }
    if (log.type !== 'pageview') {
      continue;
    }
    if (uniquedElapsedDays.indexOf(elaplsedDays) === -1) {
      uniquedElapsedDays.push(elaplsedDays);
    }
  }
  const days = uniquedElapsedDays.length;
  // PageViewログデータがない場合は1を返す。
  return days === 0 ? 1 : days;
};
/**
 * 直近のコメントモジュールの閲覧日を取得
 *
 * @param logEvents
 */
const getCommentViewableDate = (logEvents: IUserBehavioralEvent[]): number | undefined => {
  for (const log of logEvents) {
    if (log.type === 'view' && log.payload.slk === 'comment') {
      return log.datetime;
    }
  }
  return undefined;
};
/**
 * ユーザのコメントモジュールの非表示設定を取得
 */
const getCommentModuleDisplayConfig = () => {
  const LOCALSTORAGE_KEY = 'YAHOO.JP.News.commentPlugin';
  const LOCALSTORAGE_VER = 1;
  const commentPluginStorage = localStorage.get<{ isShow: boolean }>(LOCALSTORAGE_KEY, LOCALSTORAGE_VER);
  if (commentPluginStorage === undefined) {
    // 他の表示系に揃えて引けない場合は表示(0)に倒す
    return '0';
  }

  return commentPluginStorage.isShow ? '0' : '1';
};

/**
 * コメントのGood/Badの押下回数を計測
 * 押下キャンセル分はマイナスする
 * Good/Badは特に区別せずまとめて計算する
 *
 * @param logEvents
 */
const getCommentVoteCount = (logEvents: IUserBehavioralEvent[]): number => {
  const clickCount = logEvents.filter(
    log =>
      log.type === 'click' &&
      (log.payload.sec === 'cmt' || log.payload.sec === 'cmt_usr') &&
      log.payload.slk &&
      ['agbtn', 'disbtn'].indexOf(log.payload.slk) !== -1
  ).length;
  const cancelCount = logEvents.filter(
    log =>
      log.type === 'click' &&
      (log.payload.sec === 'cmt' || log.payload.sec === 'cmt_usr') &&
      log.payload.slk &&
      ['agbtn_cancel', 'disbtn_cancel'].indexOf(log.payload.slk) !== -1
  ).length;
  const voteCount = clickCount - cancelCount;
  return voteCount > 0 ? voteCount : 0;
};

/**
 * コメント投稿件数を計測
 *
 * @param logEvents
 */
const getCommentPostCount = (logEvents: IUserBehavioralEvent[]): number => {
  return logEvents.filter(
    log => log.type === 'click' && (log.payload.sec === 'artcmt_f' || log.payload.sec === 'cmt_u_f')
  ).length;
};
